import { BetSlipType } from '@bpos';
import { OverAskOfferDetails } from '@bpos/common/bet-placement';
import { EdsPromotionToken, PromotionToken, SignPost, SystemBetDetails } from '@bpos/v1/my-bets';
import { Odds } from '@frontend/sports/odds/feature';
import { OptionBetDetails, ParticipantBetDetails, PlaceBetStatus, SlipOptionType } from '@frontend/sports/types/betslip';
import { RewardPoints } from '@frontend/sports/types/models/loyalty';

import { BaseBetslipGroupPick } from '../../core/picks/betslip-group-pick';
import { BetslipPick } from '../../core/picks/betslip-pick';
import { PickId } from '../../core/picks/pick-id';
import { SlipId } from '../bet-generation/models';
import { BetPlacementError } from '../validation/errors/bet-placement-error';
import { UnderMinimumStakePreCheckError } from '../validation/errors/pre-check/under-minimum-stake-pre-check-error';
import { ResultError } from '../validation/errors/result/result-error';

export enum SlipResultStatus {
    Success,
    Fail,
}

export type SlipResult = ISucceededSlipResult | ISucceededPartialSlipResult | IFailedSlipResult;

export interface IPlacedSlipResult {
    status: SlipResultStatus;
}

export interface ISucceededSlipPickResult {
    odds: Odds;
    eventName: string;
    marketName: string;
    optionNames: string[];
    slipOptionType: SlipOptionType;
    pickId: PickId;
    clientPick?: BetslipPick;
    participantBetDetails?: ParticipantBetDetails;
    isTeaserBet?: boolean;
    totalOdds: Odds;
    optionBetDetails?: OptionBetDetails;
}

export interface ISucceededPartialSlipResult extends IPlacedSlipResult {
    status: SlipResultStatus.Success;
    betNumber: string;
    conclusionDate: Date;
}

export interface ISucceededSlipResult extends IPlacedSlipResult {
    status: SlipResultStatus.Success;
    betNumber: string;
    betType: BetSlipType;
    currency?: string;
    stake?: number;
    totalStake?: number;
    totalOdds?: Odds;
    boostedOdds?: Odds;
    possibleWinnings?: {
        net: number;
        gross: number;
    };
    picks: ISucceededSlipPickResult[];
    rewardUsed: boolean;
    isFreebet: boolean;
    promoTokens: PromotionToken[];
    conclusionDate: Date;
    edsPromoTokens: EdsPromotionToken[];
    systemBetDetails: SystemBetDetails | undefined;
    systemKey?: string;
    isMultiBB: boolean;
    isMultiTeaser?: boolean;
    betCount: number;
    signPostings: SignPost[];
}

export interface IFailedSlipPickResult {
    index: number;
    clientPick: BetslipPick;
    errors: ResultError[];
}

export interface IFailedSlipResult extends IPlacedSlipResult {
    status: SlipResultStatus.Fail;
    picks: { [pickId: string]: IFailedSlipPickResult };
    errors: BetPlacementError[];
    overAskOffer?: OverAskOfferDetails;
    betType?: BetSlipType;
    details?: IFailedSlipDetails;
}

export enum FailedSlipDetailsType {
    Teaser = 'teaser',
    BetBuilder = 'betbuilder',
}

export interface IFailedSlipDetails {
    type: FailedSlipDetailsType;
}

export interface IFailedSlipTeaserDetails extends IFailedSlipDetails {
    type: FailedSlipDetailsType.Teaser;
    isTeaserBet: boolean;
}

export interface IFailedSlipBetBuilderDetails extends IFailedSlipDetails {
    type: FailedSlipDetailsType.BetBuilder;
    betBuilderPickId: PickId;
}

export interface IPlacedBetslipResult {
    status: PlaceBetStatus;
    resultMessage: string;
    slips: SlipResult[];
    teasedPoints?: number;
    rewardPoints?: RewardPoints;
}

export interface IPlaceBetButtonMessages {
    loginToBet: string;
    makeADeposit: string;
    depositAndPlaceBet: string;
    acceptChanges: string;
    acceptAndPlaceBet: string;
    placeProcessing: string;
    placeBet: string;
    lockedBetsToRemove: string;
    depositProcessing: string;
    depositAndBetProcessing: string;
    placeFreeBet: string;
    acceptAndPlaceFreeBet: string;
    confirm: string;
    RemoveSuspendedLegsToPlaceBet: string;
    updateMinStake: string;
    updateMinStakes: string;
}

export interface IBetPicks {
    picks: { [betIndex: number]: BetslipPick };
    groups: { [groupIndex: number]: BaseBetslipGroupPick };
}

export interface StakeUpdateErrorModel {
    error: UnderMinimumStakePreCheckError;
    slipId: SlipId;
}

export enum GeoComplyKeys {
    GeoComplyRegionCode = 'geoComplyRegionCode',
    GeoComplyUuid = 'geoComplyUUID',
    GeoComplySdkSolution = 'geoComplySdkSolution',
    GeoComplyRegion = 'geoComplyRegion',
    GeoComplyOs = 'geoComplyOS',
}
